import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const BLUE = '#004aad';

const pad = (n) => String(n).padStart(2, '0');
const getMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  return { start: `${y}-${pad(m + 1)}-01`, end: `${y}-${pad(m + 1)}-${pad(last)}` };
};

// Shared styles
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '250px 1fr',
  alignItems: 'start',
  marginBottom: '12px'
};
const labelStyle = {
  fontWeight: 600,
  color: '#1a1a1a',
  fontSize: '15px',
  paddingTop: '8px',
  margin: 0
};
const inputStyle = {
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: '4px',
  color: '#555',
  fontSize: '14px',
  height: '36px',
  padding: '4px 12px',
  boxShadow: 'none'
};

function CasesShippedReportForm() {
  const range = getMonthRange();
  const [startDate, setStartDate] = useState(range.start);
  const [endDate, setEndDate] = useState(range.end);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountTags, setSelectedAccountTags] = useState('');
  const [excludeAlreadyPrinted, setExcludeAlreadyPrinted] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountTags, setAccountTags] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState('');
  const [, setCases] = useState([]);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setExcludeAlreadyPrinted(checked);
      return;
    }

    switch (name) {
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      case 'account':
        setSelectedAccount(value);
        setSelectedAccountTags(''); // Reset selected tags when account changes
        break;
      case 'accountTags':
        setSelectedAccountTags(value);
        break;
      default:
        break;
    }
  };

  // Fetch accounts from API
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await fetch('https://hms.backend.hiragroupcloud.com/api/account/');
        if (!response.ok) {
          throw new Error('Failed to fetch accounts.');
        }
        const data = await response.json();
        setAccounts(data.data || []);
        setLoadingAccounts(false);
      } catch (error) {
        setError('Error fetching accounts.');
        setLoadingAccounts(false);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch account tags from API
  useEffect(() => {
    const fetchAccountTags = async () => {
      try {
        const response = await fetch('https://hms.backend.hiragroupcloud.com/api/cases/');
        if (!response.ok) {
          throw new Error('Failed to fetch account tags.');
        }
        const data = await response.json();

        setAccountTags(data || []);
        setLoadingTags(false);
      } catch (error) {
        setError('Error fetching account tags.');
        setLoadingTags(false);
      }
    };
    fetchAccountTags();
  }, []);

  // Helper function to format date to 'YYYY-MM-DD'
  const formatDate = (date) => {
    return new Date(date).toISOString().split('T')[0];
  };

  // Clean the tags (remove quotes and spaces)
  const removeQuotes = (tag) => {
    return tag?.replace(/['"]+/g, '').trim();
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const fetchCases = async () => {
      try {
        const response = await fetch('https://hms.backend.hiragroupcloud.com/api/cases/');
        if (!response.ok) {
          throw new Error('Failed to fetch cases.');
        }
        const data = await response.json();

        // Filter cases based on the selected criteria
        const filteredCases = data.filter((caseItem) => {
          const formattedStartDate = formatDate(startDate);
          const formattedEndDate = formatDate(endDate);
          const caseStartDate = formatDate(caseItem.start_date);
          const caseEndDate = formatDate(caseItem.arrive_date);

          // Compare dates
          const isStartDateMatch = caseStartDate === formattedStartDate;
          const isEndDateMatch = caseEndDate === formattedEndDate;
          const isAccountMatch = selectedAccount ? caseItem.account_holder === selectedAccount : true;

          // Clean the tags (remove quotes and trim)
          const cleanedTags = removeQuotes(caseItem.tags);
          const cleanedSelectedTag = selectedAccountTags;

          // Compare tags
          const isTagsMatch = selectedAccountTags ? cleanedTags === cleanedSelectedTag : true;

          return isStartDateMatch && isEndDateMatch && isAccountMatch && isTagsMatch;
        });

        setCases(filteredCases);
        navigate('/Addcase', { state: { cases: filteredCases } });
      } catch (error) {
        setError('Error fetching cases.');
      }
    };

    fetchCases();
  };

  return (
    <div style={{ maxWidth: '500px', marginLeft: '20px', marginTop: '20px', fontFamily: 'inherit' }}>
      <h3
        style={{
          color: BLUE,
          fontWeight: 300,
          fontSize: '22px',
          margin: 0,
          paddingBottom: '2px',
          borderBottom: `2px solid ${BLUE}`
        }}
      >
        Parameters
      </h3>

      <div style={{ backgroundColor: '#ececec', padding: '20px 10px 24px', borderRadius: '0 0 3px 3px' }}>
        <Form onSubmit={handleSubmit}>
          {/* Start Date */}
          <div style={rowStyle}>
            <label style={labelStyle}>
              Start Date <span style={{ color: 'red' }}>*</span>
            </label>
            <div>
              <Form.Control
                type="date"
                name="startDate"
                value={startDate}
                onChange={handleInputChange}
                required
                style={{ ...inputStyle, width: '168px' }}
              />
            </div>
          </div>

          {/* End Date */}
          <div style={rowStyle}>
            <label style={labelStyle}>
              End Date <span style={{ color: 'red' }}>*</span>
            </label>
            <div>
              <Form.Control
                type="date"
                name="endDate"
                value={endDate}
                onChange={handleInputChange}
                required
                style={{ ...inputStyle, width: '168px' }}
              />
            </div>
          </div>

          {/* Accounts Dropdown */}
          <div style={rowStyle}>
            <label style={labelStyle}>Accounts</label>
            <div>
              <Form.Control
                as="select"
                name="account"
                value={selectedAccount}
                onChange={handleInputChange}
                style={{ ...inputStyle, width: '190px' }}
              >
                <option value="">All</option>
                {loadingAccounts && <option value="" disabled>Loading accounts...</option>}
                {error && <option value="" disabled>{error}</option>}
                {!loadingAccounts && accounts.length > 0 &&
                  accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.account_name}
                    </option>
                  ))}
              </Form.Control>
            </div>
          </div>

          {/* Account Tags Dropdown */}
          <div style={rowStyle}>
            <label style={labelStyle}>Account Tags</label>
            <div>
              <Form.Control
                as="select"
                name="accountTags"
                value={selectedAccountTags}
                onChange={handleInputChange}
                style={{ ...inputStyle, width: '190px', color: '#333', appearance: 'none', backgroundImage: 'none' }}
              >
                <option value="">No filter</option>
                {loadingTags && <option value="" disabled>Loading tags...</option>}
                {error && <option value="" disabled>{error}</option>}
                {!loadingTags && accountTags.length > 0 &&
                  accountTags.map((tag) => (
                    <option key={tag._id} value={tag.tags}>
                      {removeQuotes(tag.tags)}
                    </option>
                  ))}
              </Form.Control>
            </div>
          </div>

          {/* Checkbox: Exclude Already Printed */}
          <div style={{ ...rowStyle, marginBottom: '24px' }}>
            <span />
            <Form.Check
              type="checkbox"
              id="excludeAlreadyPrinted"
              name="excludeAlreadyPrinted"
              label="Exclude Already Printed"
              checked={excludeAlreadyPrinted}
              onChange={handleInputChange}
              style={{ color: '#222', fontSize: '15px' }}
            />
          </div>

          {/* Run Button */}
          <button
            type="submit"
            style={{
              marginLeft: '30px',
              backgroundColor: BLUE,
              border: `1px solid ${BLUE}`,
              color: '#fff',
              padding: '8px 12px',
              fontSize: '14px',
              borderRadius: '3px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
            Run
          </button>
        </Form>
      </div>
    </div>
  );
}

export default CasesShippedReportForm;